import { AtsJobPostingSalaryPeriodEnum } from 'generated/resources/datatable.graphql'
import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'

import {
  CONTRACT_TYPES,
  JOB_STATUSES,
  type JobStatus,
  jobContractTypeLabel,
  jobScheduleTypeLabel,
  jobStatusLabel,
} from 'modules/ats/lib/utils/jobPosting'
import applicationPhases from 'modules/ats/store/collections/applicationPhases'
import applications from 'modules/ats/store/collections/applications'
import type Application from 'modules/ats/store/models/Application'
import type ApplicationPhase from 'modules/ats/store/models/ApplicationPhase'
import type { WorkplaceType } from 'modules/ats/types/job'
import type { ModelWithCustomFieldIdentifier } from 'modules/custom_resources/store/collections/customFields/byGroup'
import locations from 'modules/locations/store/collections/locations'
import type Location from 'modules/locations/store/models/Location'
import type { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'

export { JOB_STATUSES }

const REF = 'ats-job_posting'

type ContractType = keyof typeof CONTRACT_TYPES
export type ScheduleType = 'full_time' | 'part_time'
type RequirementType = 'mandatory' | 'optional' | 'do_not_ask'
export type PublishedType = 'published' | 'unlisted'

export interface IJob {
  id: number
  company_id?: number | null
  creation_date: string
  title: string
  description?: string | null
  contract_type?: ContractType | null
  status: JobStatus
  schedule_type?: ScheduleType | null
  team_id?: number | null
  location_id?: number | null
  workplace_type: WorkplaceType
  legal_entity_id?: number | null
  ats_company_id: number
  salary_format?: string | null
  salary_from_amount_in_cents?: number | null
  salary_to_amount_in_cents?: number | null
  salary_permission?: boolean | null
  hide_salary?: boolean | null
  cv_requirement: RequirementType
  cover_letter_requirement: RequirementType
  phone_requirement: RequirementType
  photo_requirement: RequirementType
  personal_url_requirement: RequirementType
  new_editor: boolean | false
  applications_count: number
  active_applications_count: number
  allow_internal_apply: boolean
  allow_referral: boolean
  salary_period: AtsJobPostingSalaryPeriodEnum
}

class Job extends Model<IJob> implements ModelWithCustomFieldIdentifier, PolicyTarget {
  static modelRef = REF

  constructor(attr: IJob) {
    super(attr)
    makeObservable(this, {
      applicationPhases: computed,
      editableApplicationPhases: computed,
      applications: computed,
      qualifiedApplications: computed,
      disqualifiedApplications: computed,
      location: computed,
      i18nStatus: computed,
      i18nContractType: computed,
      i18nScheduleType: computed,
      allowsNewCandidates: computed,
      isPublished: computed,
      isArchived: computed,
      isDraft: computed,
      isUnlisted: computed,
      isOpen: computed,
      isCancelled: computed,
    })
  }

  static urlRoot() {
    return '/ats/job_postings'
  }

  customFieldsIdentifier(): string {
    return REF
  }

  get applicationPhases(): ApplicationPhase[] {
    return applicationPhases.filter({
      ats_job_posting_id: this.attributes.id,
    })
  }

  get editableApplicationPhases(): ApplicationPhase[] {
    return applicationPhases.filter({
      ats_job_posting_id: this.attributes.id,
      editable: true,
    })
  }

  get applications(): Application[] {
    return applications.filter({
      ats_job_posting_id: this.attributes.id,
    })
  }

  get qualifiedApplications(): Application[] {
    return applications.filter({
      ats_job_posting_id: this.attributes.id,
      qualified: true,
    })
  }

  get disqualifiedApplications(): Application[] {
    return applications.filter({
      ats_job_posting_id: this.attributes.id,
      qualified: false,
    })
  }

  get location(): Location | null | undefined {
    return locations.find({
      id: this.attributes.location_id,
    })
  }

  get i18nStatus(): string {
    return jobStatusLabel(this.attributes.status)
  }

  get i18nContractType(): string {
    return jobContractTypeLabel(this.attributes.contract_type)
  }

  get i18nScheduleType(): string {
    return jobScheduleTypeLabel(this.attributes.schedule_type)
  }

  isCurrentStatus(status: JobStatus) {
    return this.attributes.status === status
  }

  get allowsNewCandidates() {
    return this.isDraft || this.isPublished || this.isUnlisted
  }

  get isPublished() {
    return this.isCurrentStatus(JOB_STATUSES.published)
  }

  get isArchived() {
    return this.isCurrentStatus(JOB_STATUSES.archived)
  }

  get isUnlisted() {
    return this.isCurrentStatus(JOB_STATUSES.unlisted)
  }

  get isOpen() {
    return this.isPublished || this.isUnlisted
  }

  get isDraft() {
    return this.isCurrentStatus(JOB_STATUSES.draft)
  }

  get isCancelled() {
    return this.isCurrentStatus(JOB_STATUSES.cancelled)
  }

  async changeStatus(status: string) {
    await this.save({
      status,
    })
    dispatch({
      type: EventTypes.JOB_POSTING_STATUS_CHANGED,
    })

    return this
  }

  async preview() {
    const value = await this.rpc('preview')
    window.open(value, '_blank')
  }

  get policyTargetClass(): string {
    return '::Ats::JobPosting'
  }
}

export default Job
